import { parseSpeakerTurns } from "@rosc/contracts";
import { Loader2, Lock, PenLine } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type TranscriptSaveOutcome,
  useTranscriptEdit,
} from "@/hooks/visits/useTranscriptEdit";
import { formatMoment } from "@/lib/review/noteSources";
import {
  joinTranscript,
  lineStartTimes,
  splitTranscript,
} from "@/lib/transcript-edit";
import { cn } from "@/lib/utils";
import { useSpeakerLabel } from "./SpeakerTranscript";
import {
  TranscriptButton as Button,
  useTranscriptV3,
} from "./transcript-style";

const MAX_BYTES = 60000;

// Grows with its content, so each turn reads like text, not a form field.
const fit = (field: HTMLTextAreaElement | null): void => {
  if (!field) return;
  field.style.height = "auto";
  field.style.height = `${field.scrollHeight}px`;
};

const LineField = ({
  index,
  label,
  value,
  changed,
  disabled,
  onChange,
  startMs,
}: {
  index: number;
  label: string | null;
  // When the turn starts in the recording (timed transcripts only).
  startMs: number | null;
  value: string;
  changed: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
}): JSX.Element => {
  const v3 = useTranscriptV3();
  const { t } = useTranslation();
  const speakerLabel = useSpeakerLabel();
  const ref = useRef<HTMLTextAreaElement>(null);
  const turn = useMemo(
    () => (label ? parseSpeakerTurns(`${label} .`)[0] : null),
    [label],
  );
  const speaker =
    turn && turn.speaker !== null
      ? speakerLabel(turn.speaker, turn.assignment)
      : null;
  useEffect(() => {
    fit(ref.current);
  });
  useEffect(() => {
    const resize = (): void => {
      fit(ref.current);
    };
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <div
      className={cn(
        "relative rounded-lg py-1 pl-3 transition-colors sm:grid sm:gap-3",
        v3 ? "sm:grid-cols-[96px_1fr]" : "sm:grid-cols-[132px_1fr]",
        "before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full",
        changed
          ? v3
            ? "before:bg-v3-brand"
            : "before:bg-primary"
          : "before:bg-transparent",
      )}
      data-changed={changed || undefined}
      data-testid="transcript-edit-line"
    >
      {speaker || startMs !== null ? (
        <span className="mb-1 flex items-center gap-2 self-start sm:mt-2 sm:mb-0 sm:flex-col sm:items-start sm:gap-1 sm:justify-self-start">
          {speaker && (
            <span
              className={cn(
                "inline-flex h-6 items-center rounded-full px-2.5 text-xs font-semibold",
                v3
                  ? "bg-v3-surface-2 font-v3 text-v3-text-2 shadow-v3-hairline"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {speaker}
            </span>
          )}
          {startMs !== null && (
            <span
              className={cn(
                "font-mono text-[11.5px] tabular-nums",
                v3 ? "text-v3-text-3" : "text-muted-foreground",
              )}
              data-testid="transcript-edit-time"
            >
              {formatMoment(startMs)}
            </span>
          )}
        </span>
      ) : (
        <span aria-hidden className="hidden sm:block" />
      )}
      <textarea
        ref={ref}
        aria-label={
          speaker
            ? t("visit.transcriptEdit.lineWithSpeaker", {
                speaker,
                number: index + 1,
              })
            : t("visit.transcriptEdit.line", { number: index + 1 })
        }
        className={cn(
          "block min-h-11 w-full resize-none overflow-hidden rounded-md border border-transparent bg-transparent px-2 py-2 text-[15px] leading-relaxed outline-hidden transition-colors disabled:opacity-60 sm:min-h-0",
          v3
            ? "rounded-[10px] font-v3 text-v3-text hover:border-v3-border-strong focus:border-v3-brand focus:bg-v3-panel focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            : "text-foreground hover:border-border focus:border-ring focus:bg-background focus-visible:ring-2 focus-visible:ring-ring/30",
        )}
        disabled={disabled}
        rows={1}
        spellCheck
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          fit(event.target);
        }}
      />
    </div>
  );
};

type Props = {
  visitPublicId: string;
  text: string;
  // Timed segments of this transcript (v3.2), to show each turn's time.
  segments?: readonly { startMs: number; text: string }[];
  // The parent asks before closing a dirty editor (sheet close, collapse).
  askDiscard: boolean;
  onAskDiscardChange: (ask: boolean) => void;
  onDirtyChange: (dirty: boolean) => void;
  onCancel: () => void;
  onSaved: (change: { before: string; after: string }) => void;
};

// "Edit transcript": one editable field per line (speaker turn), the speaker
// label kept as a chip. Save writes a new transcript version; the original
// stays in the visit's version history.
export const TranscriptEditor = ({
  visitPublicId,
  text,
  askDiscard,
  onAskDiscardChange,
  onDirtyChange,
  onCancel,
  onSaved,
  segments,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const v3 = useTranscriptV3();
  // The text the edit started from; a refresh of the visit does not reset it.
  const [base] = useState(text);
  const lines = useMemo(() => splitTranscript(base), [base]);
  const [edited, setEdited] = useState(() => lines.map((line) => line.text));
  const times = useMemo(
    () => lineStartTimes(lines, segments ?? []),
    [lines, segments],
  );
  const [problem, setProblem] = useState<
    Exclude<TranscriptSaveOutcome, "saved" | "unchanged"> | "empty" | "long"
  >();
  const { save, saving } = useTranscriptEdit(visitPublicId);
  const changedLines = edited.filter(
    (value, index) => value !== lines[index]?.text,
  ).length;
  const dirty = changedLines > 0;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  // Leaving the page with unsaved corrections asks first.
  useEffect(() => {
    if (!dirty) return;
    const guard = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", guard);
    return () => {
      window.removeEventListener("beforeunload", guard);
    };
  }, [dirty]);

  const handleSave = async (): Promise<void> => {
    const next = joinTranscript(lines, edited);
    if (!next.trim()) {
      setProblem("empty");
      return;
    }
    if (new TextEncoder().encode(next).length > MAX_BYTES) {
      setProblem("long");
      return;
    }
    setProblem(undefined);
    const outcome = await save(base, next);
    if (outcome === "saved") onSaved({ before: base, after: next });
    else if (outcome === "unchanged") onCancel();
    else setProblem(outcome);
  };

  const visible = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => line.original.trim() || line.label);

  return (
    <div className="space-y-3 py-3" data-testid="transcript-editor">
      <p
        className={cn(
          "flex items-center gap-2 rounded-[10px] px-3 py-2 text-[12.5px]",
          v3
            ? "bg-v3-surface-2 font-v3 text-v3-text-2"
            : "bg-muted/60 text-muted-foreground",
        )}
      >
        <PenLine aria-hidden className="size-3.5 shrink-0" />
        {t("visit.transcriptEdit.hint")}
      </p>
      <div className="space-y-0.5">
        {visible.map(({ line, index }) => (
          <LineField
            key={index}
            changed={edited[index] !== line.text}
            disabled={saving}
            index={index}
            label={line.label}
            startMs={times[index] ?? null}
            value={edited[index] ?? ""}
            onChange={(value) => {
              setProblem(undefined);
              setEdited((current) =>
                current.map((item, at) => (at === index ? value : item)),
              );
            }}
          />
        ))}
      </div>
      <p
        className={cn(
          "flex items-start gap-2 text-xs",
          v3 ? "font-v3 text-v3-text-3" : "text-muted-foreground",
        )}
      >
        <Lock aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        {t("visit.transcriptEdit.kept")}
      </p>
      {problem && (
        <div
          className={cn(
            "flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm",
            v3
              ? "rounded-xl bg-v3-rec-tint font-v3 text-v3-rec-text"
              : "border border-destructive/40 bg-destructive/10 text-destructive",
          )}
          role="alert"
        >
          {t(`visit.transcriptEdit.problem.${problem}`)}
          {problem === "stale" && (
            <Button
              className="h-11 sm:h-8"
              size="sm"
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              {t("visit.transcriptEdit.loadLatest")}
            </Button>
          )}
        </div>
      )}
      <div
        className={cn(
          "sticky bottom-0 -mx-1 flex flex-wrap items-center gap-2 border-t px-1 py-2 backdrop-blur-sm",
          v3
            ? "border-v3-border bg-v3-panel/95 font-v3 text-v3-text"
            : "bg-card/95",
        )}
        data-testid="transcript-edit-actions"
      >
        {askDiscard ? (
          <>
            <p className="min-w-0 flex-1 basis-full text-sm font-medium sm:basis-auto">
              {t("visit.transcriptEdit.discardTitle")}
            </p>
            <Button
              className="h-11 flex-1 sm:h-9 sm:flex-none"
              type="button"
              variant="outline"
              onClick={() => {
                onAskDiscardChange(false);
              }}
            >
              {t("visit.transcriptEdit.keepEditing")}
            </Button>
            <Button
              className="h-11 flex-1 sm:h-9 sm:flex-none"
              type="button"
              variant="destructive"
              onClick={onCancel}
            >
              {t("visit.transcriptEdit.discard")}
            </Button>
          </>
        ) : (
          <>
            <span
              aria-live="polite"
              className="min-w-0 flex-1 basis-full text-xs text-muted-foreground sm:basis-auto"
            >
              {dirty
                ? t("visit.transcriptEdit.changedLines", {
                    count: changedLines,
                  })
                : t("visit.transcriptEdit.noChanges")}
            </span>
            <Button
              className="h-11 flex-1 sm:h-9 sm:flex-none"
              disabled={saving}
              type="button"
              variant="outline"
              onClick={() => {
                if (dirty) onAskDiscardChange(true);
                else onCancel();
              }}
            >
              {t("visit.transcriptEdit.cancel")}
            </Button>
            <Button
              className="h-11 flex-1 sm:h-9 sm:flex-none"
              disabled={!dirty || saving}
              type="button"
              onClick={() => void handleSave()}
            >
              {saving && <Loader2 className="animate-spin" />}
              {t(
                saving
                  ? "visit.transcriptEdit.saving"
                  : "visit.transcriptEdit.save",
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
